/**
 * HeadlineStack Component
 */

import React, { useMemo } from 'react';
import { HeadlineStackProps, HeadlineStackData } from './headlineStack.types';
import {
  HeadlineStackWrapper,
  HeadlineItemWrapper,
  HeadlineWrapper,
  LoadingWrapper,
  LoadingTextWrapper,
} from './headlineStack.style';
import Spot from '../Spot/spot';
import { SpotType, SpotProps } from '../Spot/spot.types';
import Link from '../Link/link';
import Skeleton from '../Skeleton/skeleton';

const emptyList: HeadlineStackData[] = [];
const defaultSpotProps: SpotProps = {
  id: 1,
  type: SpotType.SPORT,
  alt: 'spot',
  width: 36,
};

const HeadlineStack = ({
  list = emptyList,
  spotProps = defaultSpotProps,
  handlers,
  disableImages = false,
  isLoading = false,
  skeletonNumber = 5,
  bordersEnabled = false,
}: HeadlineStackProps) => {
  const renderedList = useMemo(() => {
    // Load this with passed number of skeletons if loading
    if (isLoading) {
      const skeletonList = new Array(skeletonNumber).fill(0);
      return skeletonList.map((fakeHeadline, index) => (
        <LoadingWrapper key={`morecoverageLoading-${index}`}>
          {!disableImages ? <Skeleton isCircle height="36px" width="36px" margin={0} /> : null}
          <LoadingTextWrapper>
            <Skeleton height="18px" width="80%" />
          </LoadingTextWrapper>
        </LoadingWrapper>
      ));
    }

    return list.map(({ headline, link, id, dataAttr }, i) => {
      // Create a set of Spot props based on the defaults passed as well as
      // data from each headline object passed
      const combinedSpotProps: SpotProps = {
        ...spotProps,
        id: id ?? spotProps.id,
        type: id ? SpotType.TEAM : SpotType.SPORT,
        alt: headline || spotProps.alt,
      };
      return (
        <li key={`headline-stack-li-${i}`}>
          <Link
            target={'_blank'}
            url={link}
            handlers={handlers}
            ariaLabel={headline}
            dataAttr={dataAttr}
            key={`headline-${i}`}
          >
            <HeadlineItemWrapper>
              {!disableImages ? <Spot {...combinedSpotProps} /> : null}
              <HeadlineWrapper>{headline}</HeadlineWrapper>
            </HeadlineItemWrapper>
          </Link>
        </li>
      );
    });
  }, [spotProps, list, handlers, disableImages, isLoading]);

  return <HeadlineStackWrapper bordersEnabled={bordersEnabled}>{renderedList}</HeadlineStackWrapper>;
};

export default HeadlineStack;
